import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Button, IconButton, Popover } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import { buttonPrimaryBg, buttonHoverDenium } from "../../constants/colors";

const DateRangePicker = ({
  open,
  anchorEl,
  onClose,
  onApply,
  currentRange,
  apiDateRange,
  userRole,
}) => {
  const [startDate, setStartDate] = useState(
    currentRange.startDate ? dayjs(currentRange.startDate).toDate() : null,
  );
  const [endDate, setEndDate] = useState(
    currentRange.endDate ? dayjs(currentRange.endDate).toDate() : null,
  );

  const minDate = useMemo(() => {
    if (apiDateRange?.minDate) return dayjs(apiDateRange.minDate).toDate();
    return undefined;
  }, [apiDateRange]);

  const maxDate = useMemo(() => {
    if (apiDateRange?.maxDate) return dayjs(apiDateRange.maxDate).toDate();
    return new Date();
  }, [apiDateRange]);

  useEffect(() => {
    if (open) {
      setStartDate(
        currentRange.startDate ? dayjs(currentRange.startDate).toDate() : null,
      );
      setEndDate(
        currentRange.endDate ? dayjs(currentRange.endDate).toDate() : null,
      );
    }
  }, [open, currentRange]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: {
            p: 2,
            borderRadius: "16px",
            width: "auto",
            mt: 1,
            "& .react-datepicker": {
              border: "none",
              fontFamily: "inherit",
              display: "flex",
              gap: "20px",
            },
            "& .react-datepicker__header": {
              bgcolor: "white",
              border: "none",
              pb: 0,
            },
            "& .react-datepicker__current-month": {
              fontWeight: 700,
              color: "#333",
              fontSize: "16px",
              mb: 2,
            },
            "& .react-datepicker__day-name": {
              color: "#666",
              fontWeight: 500,
              width: "38px",
              margin: "2px",
            },
            "& .react-datepicker__day": {
              width: "40px",
              height: "40px",
              lineHeight: "40px",
              margin: "2px",
              borderRadius: "50%",
              fontSize: "14px",
              position: "relative",
              zIndex: 1,
              color: "#333",
              "&:hover": {
                bgcolor: "#f0f0f0 !important",
                borderRadius: "50%",
              },
            },
            "& .react-datepicker__day--outside-month": {
              color: "#ccc !important",
              pointerEvents: "none",
              visibility: "hidden",
            },
            "& .react-datepicker__day--in-range": {
              bgcolor: "#e3f2fd !important",
              color: "#333 !important",
              borderRadius: "0 !important",
              margin: "2px 0",
              width: "44px",
              "&:hover": {
                borderRadius: "50% !important",
                bgcolor: "#1976d2 !important",
              },
            },
            "& .react-datepicker__day--range-start": {
              bgcolor: "#1976d2 !important",
              color: "white !important",
              borderRadius: "50% !important",
              width: "40px",
              margin: "2px",
              "&::after": {
                content: '""',
                position: "absolute",
                top: 0,
                right: "-4px",
                bottom: 0,
                width: "50%",
                bgcolor: "#1976d2",
                zIndex: -1,
              },
            },
            "& .react-datepicker__day--range-end": {
              bgcolor: "#1976d2 !important",
              color: "white !important",
              borderRadius: "50% !important",
              width: "40px",
              margin: "2px",
              "&::after": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-4px",
                bottom: 0,
                width: "50%",
                bgcolor: "#1976d2",
                zIndex: -1,
              },
            },
            "& .react-datepicker__day--range-start.react-datepicker__day--range-end":
              {
                "&::after": {
                  display: "none",
                },
              },
            "& .react-datepicker__day--in-selecting-range": {
              bgcolor: "#e3f2fd !important",
              borderRadius: "0 !important",
              color: "#333 !important",
            },
            "& .react-datepicker__month-container": {
              "&:first-of-type": {
                borderRight: "1px solid #efefef",
                pr: 2,
              },
            },
            "& .react-datepicker__day--disabled": {
              color: "#a8b3bfff !important",
            },
          },
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
        px={1}
      >
        <Typography variant="h6" fontWeight={600} color={buttonPrimaryBg}>
          Select Date Range
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
        <DatePicker
          selected={startDate}
          onChange={(update) => {
            const [start, end] = update;
            setStartDate(start);
            setEndDate(end);
          }}
          startDate={startDate}
          endDate={endDate}
          selectsRange
          inline
          minDate={minDate}
          maxDate={maxDate}
          monthsShown={2}
          showPreviousMonths={
            !startDate &&
            !(
              minDate &&
              maxDate &&
              dayjs(maxDate).isSame(dayjs(minDate), "month")
            )
          }
        />
      </Box>
      <Box display="flex" gap={2} px={1}>
        <Button
          variant="contained"
          disabled={
            !startDate ||
            !endDate ||
            (currentRange.startDate === dayjs(startDate).format("YYYY-MM-DD") &&
              currentRange.endDate === dayjs(endDate).format("YYYY-MM-DD"))
          }
          onClick={() => {
            onApply({
              startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : "",
              endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : "",
            });
            onClose();
          }}
          sx={{
            bgcolor: buttonPrimaryBg,
            borderRadius: "24px",
            textTransform: "none",
            py: 1,
            px: 4,
            fontWeight: 600,
            "&:hover": { bgcolor: buttonHoverDenium },
            "&.Mui-disabled": {
              bgcolor: "#D1D5DB",
              color: "#9CA3AF",
            },
          }}
        >
          Apply
        </Button>
        <Button
          variant="outlined"
          disabled={!currentRange.startDate && !currentRange.endDate}
          onClick={() => {
            setStartDate(null);
            setEndDate(null);
            onApply({
              startDate: "",
              endDate: "",
            });
            onClose();
          }}
          sx={{
            borderRadius: "24px",
            textTransform: "none",
            py: 1,
            px: 4,
            fontWeight: 600,
            borderColor: buttonPrimaryBg,
            color: buttonPrimaryBg,
            "&:hover": {
              borderColor: buttonHoverDenium,
              bgcolor: "rgba(0, 0, 0, 0.04)",
            },
            "&.Mui-disabled": {
              borderColor: "#D1D5DB",
              color: "#9CA3AF",
            },
          }}
        >
          Clear
        </Button>
      </Box>
    </Popover>
  );
};

DateRangePicker.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  currentRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
  apiDateRange: PropTypes.shape({
    minDate: PropTypes.string,
    maxDate: PropTypes.string,
  }),
  userRole: PropTypes.string,
};

export default DateRangePicker;
